/*
    Created by Phat Pham
    phat@launchdeck.org
*/

import React, { useImperativeHandle, useRef, useState } from 'react';
import { Keyboard, ScrollView, Text, View } from 'react-native-web';
import PropTypes from 'prop-types';
import Icon from 'react-native-vector-icons/dist/MaterialIcons';

// Custom Components
import { Button, CustomModal, NavBar, TextBox } from '../../components';

// Utils
import { showAlert, showAppError } from '../../utils/util_helpers';

// Configs
import GLOBAL from '../../configs/config_global';

// APIs
import { updateTruckName } from '../../modules';

export default function EditNameModal(props, ref) {
    // region Props
    const { truckData, onChangeCompleted } = props;
    // endregion

    // region Refs
    const originalName = useRef(null);
    // endregion

    // region Local State
    const [modalVisible, setModalVisible] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [truckName, setTruckName] = useState('');
    // endregion

    // region Function - Show/Hide Modal
    const showModal = () => {
        setModalVisible(true);
        setTruckName(truckData.name);
        originalName.current = truckData.name;
    }

    const hideModal = () => {
        setModalVisible(false);
    }
    // endregion

    // region Expose Method
    useImperativeHandle(
        ref,
        () => ({ showModal, hideModal }),
        [truckData]
    );
    // endregion

    // region Function - Init Truck Name Edit
    const goChangeName = () => {
        Keyboard.dismiss();
        setLoading(true);
        updateTruckName(truckData?.id, truckName)
            .then(() => {
                // Callback
                onChangeCompleted(truckName);

                // Close screen
                hideModal();

                // Show alert
                setTimeout(() => showAlert({ text: 'Truck name updated successfully to "' + truckName + '"' }), 500);
            })
            .catch((error) => showAppError(error))
            .finally(() => setLoading(false));
    }
    // endregion

    // region MAIN RENDER
    return(
        <CustomModal visible={modalVisible}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={() => hideModal()}
                title={'Update Truck Name'}
            />

            {/* Main Content */}
            <ScrollView
                style={{ width: '100%', flex: 1, backgroundColor: 'white', alignSelf: 'stretch' }}
                contentContainerStyle={{ paddingHorizontal: 64, paddingBottom: 64, alignItems: 'center' }}>
                {/* Warning */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginVertical: 40, alignSelf: 'stretch' }}>
                    <Icon
                        name={'warning'}
                        size={24}
                        color={GLOBAL.COLOR.BASIC.GOLD}
                    />

                    <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 20, fontWeight: 'bold', marginLeft: 8, flex: 1, letterSpacing: 0.2, lineHeight: 30 }}>
                        Warning:{' '}
                        <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 20, fontWeight: 'normal', letterSpacing: 0.2, lineHeight: 30 }}>
                            Please note that changing your food truck’s name will affect its Google search ranking. If you just have minor changes, please reach out to us at <u>vendor.support@gotruckster.com</u> to find a solution.
                        </Text>
                    </Text>
                </View>

                {/* Name Input */}
                <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginBottom: 16, fontWeight: 'bold', fontSize: 22, textAlign: 'center' }}>
                    Enter your desired new name below
                </Text>

                {/* Name Select */}
                <TextBox
                    placeholder={'Name'}
                    autoCorrect={false}
                    contentBoxStyle={{ marginTop: 0 }}
                    title={'Current Name'}
                    value={truckName}
                    onChangeText={setTruckName}
                    style={{ width: '50%', alignSelf: 'center' }}
                    textInputStyle={{ fontSize: 18 }}
                    onSubmitEditing={() => Keyboard.dismiss()}
                />

                {/* Submit Button */}
                <Button
                    style={{ width: '50%', marginTop: 12, alignSelf: 'center' }}
                    text={'Confirm Name Change'}
                    isLoading={isLoading}
                    locked={!truckName?.trim() || (truckName === originalName.current)}
                    onPress={() => goChangeName()}
                />
            </ScrollView>
        </CustomModal>
    )
    // endregion
}

// Forward Ref
EditNameModal = React.forwardRef(EditNameModal);

// region Props
EditNameModal.propTypes = {
    truckData: PropTypes.object,
    onChangeCompleted: PropTypes.func
};

EditNameModal.defaultProps = {
    truckData: {},
    onChangeCompleted: () => {}
};
// endregion
